import React, { useState, useEffect } from 'react';
import { 
  FaMobile, FaTags, FaBox, FaUsers, FaShoppingCart, 
  FaMoneyBill, FaChartLine, FaBell, FaEye, FaEyeSlash 
} from 'react-icons/fa';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import StatCard from '../../components/admin/StatCard';
import { dashboardService } from '../../services/dashboardService';
import { formatCurrency } from '../../utils/helpers';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsData, salesData, expenseBreakdown, inventoryStatus] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getMonthlySales(),
        dashboardService.getExpenseBreakdown(),
        dashboardService.getInventoryStatus(),
      ]);
      
      setStats(statsData.data);
      setExpenseData(expenseBreakdown.data);
      setInventoryData(inventoryStatus.data);
      
      // Prepare sales chart data
      const months = salesData.data.map(item => item.month);
      const revenue = salesData.data.map(item => item.revenue);
      const profit = salesData.data.map(item => item.profit);
      
      setChartData({
        labels: months,
        datasets: [
          {
            label: 'Revenue',
            data: revenue,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Profit',
            data: profit,
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.4,
            fill: true,
          },
        ],
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const grossRevenue = stats?.monthly?.revenue || 0;
  const monthlyExpenses = stats?.monthly?.expenses || 0;
  const netRevenue = grossRevenue - monthlyExpenses;
  const grossProfit = stats?.monthly?.profit || 0;
  const netProfit = grossProfit - monthlyExpenses;

  const statCards = [
    { title: 'Total Products', value: stats?.totalProducts || 0, icon: FaMobile, color: 'from-blue-500 to-blue-700' },
    { title: 'Categories', value: stats?.totalCategories || 0, icon: FaTags, color: 'from-green-500 to-green-700' },
    { title: 'Brands', value: stats?.totalBrands || 0, icon: FaBox, color: 'from-purple-500 to-purple-700' },
    { title: 'Customers', value: stats?.totalCustomers || 0, icon: FaUsers, color: 'from-yellow-500 to-yellow-700' },
    { title: 'Total Sales', value: stats?.totalSales || 0, icon: FaShoppingCart, color: 'from-pink-500 to-pink-700' },
    { title: 'Gross Revenue', value: `RS:${grossRevenue.toLocaleString()}`, icon: FaMoneyBill, color: 'from-indigo-500 to-indigo-700', change: stats?.monthly?.revenueGrowth },
    { title: 'Monthly Expenses', value: `RS:${monthlyExpenses.toLocaleString()}`, icon: FaMoneyBill, color: 'from-red-500 to-red-700' },
    { title: 'Net Revenue', value: `RS:${netRevenue.toLocaleString()}`, icon: FaChartLine, color: netRevenue >= 0 ? 'from-teal-500 to-teal-700' : 'from-red-600 to-red-800' },
    { title: 'Net Profit', value: `RS:${netProfit.toLocaleString()}`, icon: FaChartLine, color: netProfit >= 0 ? 'from-green-500 to-green-700' : 'from-red-600 to-red-800' },
  ];

  // Expense pie chart data
  const expenseChartData = expenseData ? {
    labels: expenseData.map(item => item.category.charAt(0).toUpperCase() + item.category.slice(1)),
    datasets: [{
      data: expenseData.map(item => item.amount),
      backgroundColor: [
        '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', 
        '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
      ],
      borderWidth: 0,
    }],
  } : null;

  // Inventory doughnut chart data
  const inventoryChartData = inventoryData ? {
    labels: ['In Stock', 'Low Stock', 'Out of Stock'],
    datasets: [{
      data: [inventoryData.inStock, inventoryData.lowStock, inventoryData.outOfStock],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
      borderWidth: 0,
    }],
  } : null;

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Dashboard Overview</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
          <h3 className="text-xl font-semibold mb-4">Monthly Sales Overview</h3>
          {chartData && (
            <Line 
              data={chartData} 
              options={{ 
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => 'RS:' + value.toLocaleString(),
                    },
                  },
                },
              }} 
            />
          )}
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
          <h3 className="text-xl font-semibold mb-4">Expense Breakdown</h3>
          {expenseChartData ? (
            <div className="h-80">
              <Pie 
                data={expenseChartData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' },
                  },
                }} 
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">No expense data available</div>
          )}
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Status */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
          <h3 className="text-xl font-semibold mb-4">Inventory Status</h3>
          {inventoryChartData ? (
            <div className="h-64">
              <Doughnut 
                data={inventoryChartData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' },
                  },
                }} 
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">No inventory data available</div>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FaBell className="text-yellow-500" />
            Low Stock Alerts
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {stats?.alerts?.lowStockProducts?.length > 0 ? (
              stats.alerts.lowStockProducts.map((product) => (
                <div key={product._id} className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div>
                    <p className="font-semibold">{product.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{product.brand?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-600 font-bold">Stock: {product.inventory.stock}</p>
                    <p className="text-xs text-gray-500">Threshold: {product.inventory.lowStockThreshold}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No low stock products</p>
                <p className="text-sm mt-2">All products have sufficient stock</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Products */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
        <h3 className="text-xl font-semibold mb-4">Recently Added Products</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Brand</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {stats?.recentProducts?.map((product) => (
                <tr key={product._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-white">{product.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{product.brand?.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">RS:{product.pricing?.salePrice?.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      product.inventory?.stock > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {product.inventory?.stock} units
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      product.status?.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {product.status?.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;